'use client';

import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';

import { scheduleIdleTask } from '@repo/frontend-shared/utils';

import {
  AnalyticsContext,
  initializeConsentAwareClarity,
  initializeMetaPixel,
  requestBrowserAnalytics,
} from './analytics-context';
import { useCookieConsent } from './cookie-consent';

interface AnalyticsProviderProps {
  children: ReactNode;
  measurementId: string;
  metaPixelEnabled?: boolean;
}

interface ConsentTrackingProvidersProps {
  clarityId: string;
  initializeClarity: (clarityId: string) => void;
}

export function ConsentTrackingProviders({ clarityId, initializeClarity }: ConsentTrackingProvidersProps): null {
  const hasAnalyticsConsent = useCookieConsent('analytics');
  const hasMarketingConsent = useCookieConsent('marketing');

  useEffect(() => {
    if (!hasAnalyticsConsent) {
      return;
    }

    // Session recording is not needed for first paint: start it in an idle slot after `load`.
    return scheduleIdleTask(() => initializeConsentAwareClarity(clarityId, initializeClarity));
  }, [clarityId, hasAnalyticsConsent, initializeClarity]);

  useEffect(() => {
    if (hasMarketingConsent) {
      // The fbq stub and its queue are set up now; fbevents.js itself loads in an idle slot after `load`.
      initializeMetaPixel(true);
    }
  }, [hasMarketingConsent]);

  return null;
}

export const AnalyticsProvider = ({ children, measurementId, metaPixelEnabled = false }: AnalyticsProviderProps) => {
  const isServer = typeof window === 'undefined';
  const hasAnalyticsConsent = useCookieConsent('analytics');
  const value = useMemo(() => ({ measurementId, metaPixelEnabled }), [measurementId, metaPixelEnabled]);

  useEffect(() => {
    if (!isServer && hasAnalyticsConsent) {
      requestBrowserAnalytics(measurementId);
    }
  }, [hasAnalyticsConsent, isServer, measurementId]);

  if (isServer) {
    throw new Error('AnalyticsProvider must be used in the browser.');
  }

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};
